import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Lang = {
  code: string;
  label: string;      // native name
  english: string;    // english name (for a11y / subtitle)
  region: string;     // ISO 3166-1 alpha-2 used to render the flag
  short: string;      // 2-letter code shown in trigger
};

const languages: Lang[] = [
  { code: "en", label: "English",    english: "English",    region: "GB", short: "EN" },
  { code: "de", label: "Deutsch",    english: "German",     region: "DE", short: "DE" },
  { code: "fr", label: "Français",   english: "French",     region: "FR", short: "FR" },
  { code: "es", label: "Español",    english: "Spanish",    region: "ES", short: "ES" },
  { code: "it", label: "Italiano",   english: "Italian",    region: "IT", short: "IT" },
  { code: "pt", label: "Português",  english: "Portuguese", region: "PT", short: "PT" },
  { code: "nl", label: "Nederlands", english: "Dutch",      region: "NL", short: "NL" },
  { code: "pl", label: "Polski",     english: "Polish",     region: "PL", short: "PL" },
  { code: "ru", label: "Русский",    english: "Russian",    region: "RU", short: "RU" },
  { code: "tr", label: "Türkçe",     english: "Turkish",    region: "TR", short: "TR" },
  { code: "ro", label: "Română",     english: "Romanian",   region: "RO", short: "RO" },
  { code: "ar", label: "العربية",    english: "Arabic",     region: "SA", short: "AR" },
];

// Convert 2-letter region code into Unicode flag emoji
const flagFromRegion = (region: string) =>
  region
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const active = i18n.language?.split("-")[0] ?? "en";
  const current = languages.find((l) => l.code === active) || languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`${t("languageSwitcher.title", "Language")}: ${current.label}`}
        className="flex min-h-[44px] items-center gap-1.5 rounded-full px-2.5 text-[13px] font-medium text-foreground transition-colors hover:bg-foreground/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <Globe className="h-[15px] w-[15px]" strokeWidth={1.6} aria-hidden="true" />
        <span className="tracking-wide">{current.short}</span>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="min-w-[240px] max-h-[70vh] overflow-y-auto p-1.5"
      >
        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-2 py-1.5">
          {t("languageSwitcher.title", "Language")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {languages.map((lang) => {
          const isActive = active === lang.code;
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => { trackEvent("language_change", { from: active, to: lang.code }); i18n.changeLanguage(lang.code); }}
              className={`flex items-center gap-3 cursor-pointer rounded-md px-2 py-2 my-0.5 transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary focus:bg-primary/15"
                  : "hover:bg-muted"
              }`}
            >
              <span
                className="w-8 h-8 rounded-full bg-card border border-border/70 flex items-center justify-center text-lg leading-none shrink-0 shadow-sm"
                aria-hidden
                style={{ fontFamily: '"Twemoji Country Flags","Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji",sans-serif' }}
              >
                {flagFromRegion(lang.region)}
              </span>
              <div className="flex-1 min-w-0 leading-tight">
                <p className="text-sm font-semibold truncate">{lang.label}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {lang.english}
                </p>
              </div>
              {isActive && <Check className="w-4 h-4 text-primary shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
